import React, { useState, useRef, useEffect } from 'react';
import { api, trackEvent } from './App';
import { Download, Upload, ArrowLeft, Check } from './Icons';

const API = process.env.REACT_APP_API_URL || '';

function apiUpload(path, formData) {
  const token = localStorage.getItem('sqetrack_token') || '';
  return fetch(API + path, {
    method: 'POST',
    headers: { 'Authorization': `Bearer ${token}` },
    body: formData,
  }).then(r => r.json());
}

function downloadTemplate() {
  const token = localStorage.getItem('sqetrack_token') || '';
  fetch(API + '/api/export/template', {
    headers: { 'Authorization': `Bearer ${token}` },
  }).then(r => r.blob()).then(blob => {
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url; a.download = 'SQETrack_Template.xlsx'; a.click();
    URL.revokeObjectURL(url);
    trackEvent('template_download');
  });
}

// ── Shared preview overlay ────────────────────────────────────────────────────

function ImportPreviewModal({ updates, totalMatched, unmatched, errors, applying, onApply, onBack }) {
  const n = updates.length;
  const noDataCount = totalMatched > n ? totalMatched - n : 0;

  return (
    <div className="si-preview-overlay">
      <div className="si-preview-modal">
        <div className="si-preview-modal-head">
          <div className="si-preview-modal-title">Review import</div>
          <div className="si-preview-modal-meta">
            <span className="si-preview-modal-count">{n} topic{n !== 1 ? 's' : ''} to import</span>
            {noDataCount > 0 && (
              <span className="si-preview-modal-sub">{noDataCount} rows matched but had no data</span>
            )}
            {unmatched.length > 0 && (
              <span className="si-preview-modal-warn">{unmatched.length} not matched</span>
            )}
          </div>
        </div>

        <div className="si-preview-modal-body">
          <table className="si-modal-table">
            <colgroup>
              <col style={{ width: '14%' }} />
              <col style={{ width: '18%' }} />
              <col />
              <col style={{ width: '52px' }} />
              <col style={{ width: '158px' }} />
              <col style={{ width: '16%' }} />
              <col style={{ width: '88px' }} />
            </colgroup>
            <thead>
              <tr>
                <th>Subject area</th>
                <th>General topic</th>
                <th>Topic</th>
                <th>Conf</th>
                <th>Status</th>
                <th>Notes</th>
                <th>Updated</th>
              </tr>
            </thead>
            <tbody>
              {updates.map((u, i) => (
                <tr key={i}>
                  <td title={u.l1}>{u.l1 || '—'}</td>
                  <td title={u.l2}>{u.l2 || '—'}</td>
                  <td className="si-modal-td-topic" title={u.l3}>{u.l3 || '—'}</td>
                  <td className="si-modal-td-conf">{u.confidence != null ? `${Math.round(u.confidence * 100)}%` : '—'}</td>
                  <td title={u.status}>{u.status || '—'}</td>
                  <td title={u.notes}>{u.notes || '—'}</td>
                  <td>{u.date_updated || '—'}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>

        {(unmatched.length > 0 || (errors && errors.length > 0)) && (
          <div className="si-preview-modal-issues">
            {unmatched.length > 0 && (
              <details className="si-errors">
                <summary style={{ fontSize: 12, color: 'var(--muted)', cursor: 'pointer' }}>
                  {unmatched.length} topic{unmatched.length !== 1 ? 's' : ''} not matched — expand to see
                </summary>
                {unmatched.map((name, i) => <div key={i} className="si-error-row">{name}</div>)}
              </details>
            )}
            {errors && errors.length > 0 && (
              <details className="si-errors">
                <summary style={{ fontSize: 12, color: 'var(--muted)', cursor: 'pointer' }}>
                  {errors.length} parsing issue{errors.length !== 1 ? 's' : ''}
                </summary>
                {errors.map((e, i) => <div key={i} className="si-error-row">{e}</div>)}
              </details>
            )}
          </div>
        )}

        <div className="si-preview-modal-foot">
          <button className="ob-btn" onClick={onApply} disabled={applying || n === 0}>
            {applying ? 'Importing…' : `Import ${n} topic${n !== 1 ? 's' : ''}`}
          </button>
          <button className="scraper-discard-btn" onClick={onBack}>Back</button>
        </div>
      </div>
    </div>
  );
}

// ── Template import ────────────────────────────────────────────────────────────

function TemplateImport({ onDone }) {
  const [phase, setPhase] = useState('upload'); // upload | preview | done
  const [updates, setUpdates] = useState([]);
  const [totalMatched, setTotalMatched] = useState(0);
  const [skipped, setSkipped] = useState(0);
  const [unmatched, setUnmatched] = useState([]);
  const [errors, setErrors] = useState([]);
  const [uploading, setUploading] = useState(false);
  const [applying, setApplying] = useState(false);
  const [uploadError, setUploadError] = useState('');
  const fileRef = useRef();

  const handleFile = async (e) => {
    const file = e.target.files[0];
    if (!file) return;
    setUploading(true); setUploadError('');
    try {
      const fd = new FormData();
      fd.append('file', file);
      const res = await apiUpload('/api/import/spreadsheet?mode=template', fd);
      setUploading(false);
      if (res.error) { setUploadError(res.error); return; }
      setUpdates(res.updates || []);
      setTotalMatched(res.total_matched != null ? res.total_matched : (res.matched || 0));
      setSkipped(res.skipped || 0);
      setUnmatched(res.unmatched || []);
      setErrors(res.errors || []);
      setPhase('preview');
    } catch (err) {
      setUploading(false);
      setUploadError('Upload failed — please try again.');
    }
  };

  const apply = async () => {
    setApplying(true);
    await api('/api/import/apply', { method: 'POST', body: JSON.stringify({ updates }) });
    setApplying(false);
    trackEvent('spreadsheet_import', { source: 'template', topics_imported: updates.length });
    setPhase('done');
  };

  if (phase === 'done') {
    return (
      <div className="si-result">
        <div className="si-result-check">✓</div>
        <div className="si-result-title">Import complete</div>
        <div className="si-result-sub">{updates.length} topic{updates.length !== 1 ? 's' : ''} updated.</div>
        <button className="ob-btn" style={{ marginTop: 20 }} onClick={onDone}>Continue</button>
      </div>
    );
  }

  if (phase === 'preview') {
    return (
      <ImportPreviewModal
        updates={updates}
        totalMatched={totalMatched}
        unmatched={unmatched}
        errors={errors}
        applying={applying}
        onApply={apply}
        onBack={() => setPhase('upload')}
      />
    );
  }

  return (
    <div className="si-upload">
      <p className="si-upload-hint">
        Download the template, fill in <strong>Confidence %</strong> (0–100) for each topic you've studied.
        Status, Notes, and Last Updated are optional.
        <br /><br />
        <strong>Already have a spreadsheet?</strong> If it uses the SQETrack column format, upload it directly — the format is detected automatically.
      </p>
      <button className="settings-action-btn" style={{ marginBottom: 16 }} onClick={downloadTemplate}>
        <Download size={13} /> Download template
      </button>
      <div className="si-dropzone" onClick={() => fileRef.current?.click()}>
        <input ref={fileRef} type="file" accept=".xlsx,.csv" style={{ display: 'none' }} onChange={handleFile} />
        {uploading ? 'Parsing…' : 'Click to upload your file (.xlsx or .csv)'}
      </div>
      {uploadError && <div className="scraper-error" style={{ marginTop: 8 }}>{uploadError}</div>}
    </div>
  );
}

// ── Custom import ──────────────────────────────────────────────────────────────

const FIELD_OPTIONS = [
  { value: 'ignore',     label: 'Ignore' },
  { value: 'topic',      label: 'Topic name' },
  { value: 'confidence', label: 'Confidence %' },
  { value: 'status',     label: 'Status' },
  { value: 'notes',      label: 'Notes' },
];

function autoSuggestField(colName) {
  const n = colName.toLowerCase();
  if (/\btopic\b/.test(n) && !/general|subject|area/.test(n)) return 'topic';
  if (/confidence|score|\bpct\b|percent|rating/.test(n)) return 'confidence';
  if (/\bstatus\b/.test(n)) return 'status';
  if (/\bnotes?\b|\bcomments?\b/.test(n)) return 'notes';
  return 'ignore';
}

function CustomImport({ onDone, topicCount }) {
  const [phase, setPhase] = useState('upload'); // upload | map | preview | done
  const [headers, setHeaders] = useState([]);
  const [sampleData, setSampleData] = useState({}); // { [colName]: firstSampleValue }
  const [columnMappings, setColumnMappings] = useState({}); // { [colName]: field }
  const [file, setFile] = useState(null);
  const [updates, setUpdates] = useState([]);
  const [unmatched, setUnmatched] = useState([]);
  const [totalMatched, setTotalMatched] = useState(0);
  const [isTemplate, setIsTemplate] = useState(false); // true when file was auto-detected as template format
  const [uploading, setUploading] = useState(false);
  const [applying, setApplying] = useState(false);
  const [error, setError] = useState('');
  const fileRef = useRef();

  const handleDetect = async (e) => {
    const f = e.target.files[0];
    if (!f) return;
    setFile(f); setUploading(true); setError('');
    try {
      const fd = new FormData();
      fd.append('file', f);
      const res = await apiUpload('/api/import/spreadsheet?mode=detect', fd);
      if (res.error) { setUploading(false); setError(res.error); return; }

      const hdrs = res.headers || [];
      const sampleRows = res.sample_rows || [];

      // Backend signals this is a SQETrack-format file — route straight to template parser
      if (res.suggested_mode === 'template') {
        const fd2 = new FormData();
        fd2.append('file', f);
        const res2 = await apiUpload('/api/import/spreadsheet?mode=template', fd2);
        setUploading(false);
        if (res2.error) { setError(res2.error); return; }
        setIsTemplate(true);
        setUpdates(res2.updates || []);
        setTotalMatched(res2.total_matched != null ? res2.total_matched : (res2.matched || 0));
        setUnmatched(res2.unmatched || []);
        setPhase('preview');
        return;
      }

      // Build sample values (first non-empty value per column)
      const samples = {};
      hdrs.forEach((h, i) => {
        samples[h] = sampleRows.find(r => r[i] && r[i].trim())?.[i] || '';
      });

      // Build auto-suggested mappings
      const mappings = {};
      hdrs.forEach(h => { mappings[h] = autoSuggestField(h); });

      setUploading(false);
      setHeaders(hdrs);
      setSampleData(samples);
      setColumnMappings(mappings);
      setPhase('map');
    } catch (err) {
      setUploading(false);
      setError('Upload failed — please try again.');
    }
  };

  const handleMatch = async () => {
    if (!file) return;
    const topicCol = Object.entries(columnMappings).find(([, v]) => v === 'topic')?.[0] || '';
    const scoreCol = Object.entries(columnMappings).find(([, v]) => v === 'confidence')?.[0] || '';
    const statusCol = Object.entries(columnMappings).find(([, v]) => v === 'status')?.[0] || '';
    const notesCol = Object.entries(columnMappings).find(([, v]) => v === 'notes')?.[0] || '';
    if (!topicCol) { setError('Select a column for Topic name to continue.'); return; }
    if (!scoreCol) { setError('Select a column for Confidence % to continue.'); return; }
    setUploading(true); setError('');
    const fd = new FormData();
    fd.append('file', file);
    fd.append('topic_col', topicCol);
    fd.append('score_col', scoreCol);
    if (statusCol) fd.append('status_col', statusCol);
    if (notesCol) fd.append('notes_col', notesCol);
    const res = await apiUpload('/api/import/spreadsheet?mode=match', fd);
    setUploading(false);
    if (res.error) { setError(res.error); return; }
    setUpdates(res.updates || []);
    setUnmatched(res.unmatched || []);
    setPhase('preview');
  };

  const setMapping = (col, value) =>
    setColumnMappings(prev => ({ ...prev, [col]: value }));

  const apply = async () => {
    setApplying(true);
    await api('/api/import/apply', { method: 'POST', body: JSON.stringify({ updates }) });
    setApplying(false);
    trackEvent('spreadsheet_import', { source: 'custom', topics_imported: updates.length });
    setPhase('done');
  };

  if (phase === 'done') {
    return (
      <div className="si-result">
        <div className="si-result-check">✓</div>
        <div className="si-result-title">Import complete</div>
        <div className="si-result-sub">{updates.length} topic{updates.length !== 1 ? 's' : ''} updated.</div>
        <button className="ob-btn" style={{ marginTop: 20 }} onClick={onDone}>Continue</button>
      </div>
    );
  }

  if (phase === 'preview') {
    if (isTemplate) {
      return (
        <ImportPreviewModal
          updates={updates}
          totalMatched={totalMatched}
          unmatched={unmatched}
          errors={[]}
          applying={applying}
          onApply={apply}
          onBack={() => { setIsTemplate(false); setPhase('upload'); }}
        />
      );
    }

    // Custom format — show match list
    return (
      <div className="si-preview">
        <div className="si-preview-stats">
          <div className="si-stat"><span className="si-stat-n" style={{ color: 'var(--green)' }}>{updates.length}</span><span className="si-stat-l">matched</span></div>
          <div className="si-stat"><span className="si-stat-n" style={{ color: 'var(--amber)' }}>{unmatched.length}</span><span className="si-stat-l">not matched</span></div>
        </div>
        {updates.length > 0 && (
          <div className="si-match-list">
            {updates.slice(0, 8).map((u, i) => (
              <div key={i} className="si-match-row">
                <span className="si-match-orig">{u.topic_name}</span>
                <span className="si-match-arrow">→</span>
                <span className="si-match-to">{u.matched_to}</span>
                <span className={`si-match-tag ${u.module === 'FLK1' ? 'tag-flk1' : 'tag-flk2'}`}>{u.module}</span>
                {u.confidence != null && (
                  <span style={{ fontFamily: 'var(--mono)', fontSize: 11, color: 'var(--muted)' }}>{Math.round(u.confidence * 100)}%</span>
                )}
              </div>
            ))}
            {updates.length > 8 && <div style={{ fontSize: 12, color: 'var(--muted)', marginTop: 4 }}>…and {updates.length - 8} more</div>}
          </div>
        )}
        {unmatched.length > 0 && (
          <details className="si-errors" style={{ marginTop: 8 }}>
            <summary style={{ fontSize: 12, color: 'var(--muted)', cursor: 'pointer' }}>
              View {unmatched.length} unmatched topic{unmatched.length !== 1 ? 's' : ''}
            </summary>
            {unmatched.map((u, i) => (
              <div key={i} className="si-error-row">{u.topic_name || u}</div>
            ))}
          </details>
        )}
        {error && <div className="scraper-error" style={{ marginTop: 8 }}>{error}</div>}
        <div style={{ display: 'flex', gap: 10, marginTop: 16 }}>
          <button className="ob-btn" onClick={apply} disabled={applying || updates.length === 0}>
            {applying ? 'Importing…' : `Import ${updates.length} topic${updates.length !== 1 ? 's' : ''}`}
          </button>
          <button className="scraper-discard-btn" onClick={() => setPhase('map')}>Back</button>
        </div>
      </div>
    );
  }

  if (phase === 'map') {
    const topicMapped = Object.values(columnMappings).includes('topic');
    const confidenceMapped = Object.values(columnMappings).includes('confidence');
    const canMatch = topicMapped && confidenceMapped;

    return (
      <div className="si-map">
        <p className="si-upload-hint" style={{ marginBottom: 16 }}>
          For each column in your spreadsheet, tell us what it represents. We've made a first guess — adjust anything that looks wrong.
        </p>
        <div className="si-col-mapper">
          {headers.map(col => {
            const mapping = columnMappings[col] || 'ignore';
            const isMapped = mapping !== 'ignore';
            return (
              <div key={col} className={`si-col-row${isMapped ? ' si-col-row--mapped' : ''}`}>
                <div className="si-col-info">
                  <div className="si-col-name">{col}</div>
                  {sampleData[col] && (
                    <div className="si-col-sample">{sampleData[col]}</div>
                  )}
                </div>
                <select
                  className={`si-col-select${isMapped ? ' si-col-select--mapped' : ''}`}
                  value={mapping}
                  onChange={e => setMapping(col, e.target.value)}
                >
                  {FIELD_OPTIONS.map(opt => (
                    <option key={opt.value} value={opt.value}>{opt.label}</option>
                  ))}
                </select>
              </div>
            );
          })}
        </div>
        {!topicMapped && (
          <p style={{ fontSize: 12, color: 'var(--amber)', marginBottom: 8 }}>
            Set one column to Topic name to continue.
          </p>
        )}
        {topicMapped && !confidenceMapped && (
          <p style={{ fontSize: 12, color: 'var(--amber)', marginBottom: 8 }}>
            Set one column to Confidence % to continue.
          </p>
        )}
        {error && <div className="scraper-error" style={{ marginTop: 4, marginBottom: 8 }}>{error}</div>}
        <div style={{ display: 'flex', gap: 10, marginTop: 8 }}>
          <button className="ob-btn" onClick={handleMatch} disabled={uploading || !canMatch}>
            {uploading ? 'Matching…' : 'Match topics →'}
          </button>
          <button className="scraper-discard-btn" onClick={() => setPhase('upload')}>Back</button>
        </div>
      </div>
    );
  }

  return (
    <div className="si-upload">
      <p className="si-upload-hint">
        Upload any spreadsheet with topic names and confidence scores. SQETrack will match them to your{' '}
        {topicCount != null ? topicCount : '…'} topics.
      </p>
      <div className="si-dropzone" onClick={() => fileRef.current?.click()}>
        <input ref={fileRef} type="file" accept=".xlsx,.csv" style={{ display: 'none' }} onChange={handleDetect} />
        {uploading ? 'Reading columns…' : 'Click to upload your spreadsheet (.xlsx or .csv)'}
      </div>
      {error && <div className="scraper-error" style={{ marginTop: 8 }}>{error}</div>}
    </div>
  );
}

// ── Main SpreadsheetImport component ──────────────────────────────────────────

export default function SpreadsheetImport({ onBack, onDone }) {
  const [card, setCard] = useState(null); // null | 'template' | 'custom'
  const [topicCount, setTopicCount] = useState(null);

  useEffect(() => {
    api('/api/topics/count').then(res => {
      if (res && res.count != null) setTopicCount(res.count);
    }).catch(() => {});
  }, []);

  if (card === 'template') {
    return (
      <div className="si-wrap">
        <button className="si-back-btn" onClick={() => setCard(null)}>
          <ArrowLeft size={13} /> Back
        </button>
        <div className="si-title">Use our template</div>
        <TemplateImport onDone={onDone} />
      </div>
    );
  }

  if (card === 'custom') {
    return (
      <div className="si-wrap">
        <button className="si-back-btn" onClick={() => setCard(null)}>
          <ArrowLeft size={13} /> Back
        </button>
        <div className="si-title">Import your spreadsheet</div>
        <CustomImport onDone={onDone} topicCount={topicCount} />
      </div>
    );
  }

  return (
    <div className="si-wrap">
      {onBack && (
        <button className="si-back-btn" onClick={onBack}>
          <ArrowLeft size={13} /> Back
        </button>
      )}
      <div className="si-title">Import data</div>
      <div className="si-cards">
        <button className="si-card" onClick={() => setCard('template')}>
          <div className="si-card-icon"><Download size={22} /></div>
          <div className="si-card-title">Use our template</div>
          <div className="si-card-text">
            Download a pre-filled spreadsheet with all {topicCount != null ? topicCount : '…'} topics.
            Fill in your confidence scores, then upload it back. Also accepts a migrated spreadsheet in the same format.
          </div>
          <div className="si-card-cta">Download template →</div>
        </button>
        <button className="si-card" onClick={() => setCard('custom')}>
          <div className="si-card-icon"><Upload size={22} /></div>
          <div className="si-card-title">Import your spreadsheet</div>
          <div className="si-card-text">Already tracking topics in your own spreadsheet? Upload it and we'll match your scores to SQETrack topics.</div>
          <div className="si-card-cta">Upload spreadsheet →</div>
        </button>
      </div>
    </div>
  );
}
